import React, { useEffect, useState } from "react";
import { LiveTrainStatus } from "../../liveTrains/types";
import "./MobileTrainTooltip.scss";

type MobileTrainTooltipProps = {
  open: boolean;
  train?: LiveTrainStatus;
  onClose: () => void;
};

const formatCountdown = (expectedEpochMs: number | undefined, nowMs: number): string => {
  if (!expectedEpochMs) {
    return "-";
  }
  const deltaMs = Math.max(0, expectedEpochMs - nowMs);
  if (deltaMs < 1000) {
    return "Due";
  }
  const totalSeconds = Math.floor(deltaMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const MobileTrainTooltip = ({
  open,
  train,
  onClose,
}: MobileTrainTooltipProps): JSX.Element | null => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) {
      return;
    }
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, [open]);

  if (!open || !train) {
    return null;
  }

  return (
    <div
      className="mobile-train-tooltip-backdrop"
      onClick={onClose}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onClose();
        }
      }}
    >
      <section
        className="mobile-train-tooltip"
        onClick={(event) => event.stopPropagation()}
        aria-label={`Train ${train.serviceId} details`}
      >
        <button
          type="button"
          className="tooltip-close"
          onClick={onClose}
          aria-label="Close train details"
        >
          Close
        </button>

        <div className="tooltip-hero">
          <span className="train-id">#{train.serviceId}</span>
          <span className={`phase ${train.phase === "AtStation" ? "station" : "transit"}`}>
            {train.phase === "AtStation" ? "At Station" : "In Transit"}
          </span>
          <div className="status">{train.status}</div>
          <div className="route-line">
            <span>{train.origin}</span>
            <span className="route-track" />
            <span>{train.destination}</span>
          </div>
        </div>

        <div className="tooltip-cards">
          <div className="mini-card">
            <label>Current</label>
            <strong>{train.currentStation || "-"}</strong>
          </div>
          <div className="mini-card">
            <label>Next</label>
            <strong>{train.nextStation || "-"}</strong>
          </div>
          <div className="mini-card">
            <label>Platform</label>
            <strong>{train.platform || "-"}</strong>
          </div>
          <div className="mini-card">
            <label>ETA</label>
            <strong>{train.expectedTime || "-"}</strong>
          </div>
          <div className="mini-card">
            <label>Arrives In</label>
            <strong>{formatCountdown(train.expectedEpochMs, nowMs)}</strong>
          </div>
        </div>

        <a href={train.serviceUrl} target="_blank" rel="noreferrer" className="open-link">
          Open live service
        </a>
      </section>
    </div>
  );
};
